import React, { useMemo } from 'react';
import { Button, Card } from './ui';
import { SystemCheckinData } from './SystemCheckFlow';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

interface SystemDiagnosticProps {
    data: SystemCheckinData;
    onClose: () => void;
}

export const SystemDiagnostic: React.FC<SystemDiagnosticProps> = ({ data, onClose }) => {
    const { diagnosticState, actionCategory, battery, weather, words } = data;
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const [isSharing, setIsSharing] = React.useState(false);

    // Determine how many red (critical) checks they've had in the last 7 days
    const redChecksThisWeek = useMemo(() => {
        if (!userProfile?.thermalCheckins) return 1; // Count this current one
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        let count = 0;
        
        userProfile.thermalCheckins.forEach(c => {
            const time = new Date(c.timestamp).getTime();
            if (time >= oneWeekAgo) {
                if (c.diagnosticState === 'Red' || c.value >= 4) count++;
            }
        });
        
        // If the current one just fired, it might already be in thermalCheckins (because we wait for refreshProfile).
        // If it is, the count is correct. If it isn't, we should realistically ensure it's at least 1.
        return Math.max(1, count);
    }, [userProfile?.thermalCheckins]);

    const getDiagnosticConfig = () => {
        switch (diagnosticState) {
            case 'Green': return {
                bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900',
                icon: '🟢', title: 'System Balanced', desc: "You're in the zone. Keep this momentum."
            };
            case 'Yellow': return {
                bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900',
                icon: '🟡', title: 'High Load', desc: "You're running hot. Time for a quick cooling break."
            };
            case 'Orange': return {
                bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900',
                icon: '🟠', title: 'System Overload', desc: "You're carrying a lot right now. Let's look at some support."
            };
            case 'Red': return {
                bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-900',
                icon: '🔴', title: 'Critical Temp', desc: "Your system requires a reset. Take a breath."
            };
            default: return {
                bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-900',
                icon: '⚪', title: 'State Logged', desc: "System check complete."
            };
        }
    };
    const diag = getDiagnosticConfig();

    const handleShareToGroup = async () => {
        if (!userProfile) return;
        setIsSharing(true);
        try {
            const messageText = `I just ran a System Check.\n\nState: **${diag.title}** (${diagnosticState})\nBattery: **${battery}%**\nWords: ${words?.join(', ') || 'None'}\n\nJust dropping this here to stay accountable.`;
            
            await addDoc(collection(db, 'forum_messages'), {
                courseId: 'stronger_together',
                userId: userProfile.uid,
                userName: userProfile.nickname || userProfile.displayName,
                text: messageText,
                createdAt: serverTimestamp(),
                status: 'safe',
                reportCount: 0,
                parentId: null,
                mood: diagnosticState === 'Red' ? 'urgent' : diagnosticState === 'Green' ? 'success' : 'rant'
            });
            onClose();
            navigate('/hub?channel=stronger_together'); 
        } catch (err) {
            console.error('Failed to share to group:', err);
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <div className={`p-6 md:p-8 rounded-[32px] ${diag.bg} max-w-md mx-auto w-full border ${diag.border} shadow-2xl relative text-center animate-in scale-in z-50`}>
             <div className="mb-8">
                <div className="text-6xl mb-4 animate-bounce">{diag.icon}</div>
                <h2 className={`text-3xl font-black font-heading mb-2 ${diag.text} tracking-tight`}>{diag.title}</h2>
                <p className={`${diag.text} opacity-80 text-base font-medium`}>{diag.desc}</p>
            </div>

            <div className="mb-10 space-y-3">
                <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 ${diag.text} opacity-60`}>Recommended Action</h3>
                
                {diagnosticState === 'Red' && redChecksThisWeek >= 3 && (
                     <Card 
                         hover 
                         onClick={() => { onClose(); navigate('/buddy-matcher'); }}
                         className="p-4 border-rose-300 shadow-sm bg-white text-left flex gap-4 items-center cursor-pointer transition-transform active:scale-95 group hover:-translate-y-1"
                     >
                         <span className="text-3xl drop-shadow-sm group-hover:scale-110 transition-transform">🤝</span>
                         <div>
                             <p className="font-bold text-slate-800 tracking-tight text-lg">Request a Buddy</p>
                             <p className="text-slate-500 text-xs mt-0.5">You've tracked in the red multiple times this week. Talk to a dad who has been through this.</p>
                         </div>
                     </Card>
                )}

                {diagnosticState === 'Red' && redChecksThisWeek < 3 && actionCategory === 'A' && (
                     <Card 
                         hover 
                         className="p-5 border-rose-200 shadow-sm bg-white text-left flex gap-4 items-start relative overflow-hidden"
                     >
                         <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-bl-[100px] -z-10"></div>
                         <span className="text-3xl drop-shadow-sm z-10">🌲</span>
                         <div className="relative z-10 w-full">
                             <p className="font-bold text-slate-800 tracking-tight text-lg">The 5-Minute Reset</p>
                             <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">
                                Put your phone away. Step outside for exactly 5 minutes. Feel the air, ground yourself, and let the adrenaline clear before your next interaction.
                             </p>
                         </div>
                     </Card>
                )}

                {diagnosticState === 'Red' && redChecksThisWeek < 3 && actionCategory !== 'A' && (
                     <Card 
                         hover 
                         className="p-5 border-rose-200 shadow-sm bg-white text-left flex gap-4 items-start relative overflow-hidden"
                     >
                         <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-bl-[100px] -z-10"></div>
                         <span className="text-3xl drop-shadow-sm z-10">🧠</span>
                         <div className="relative z-10 w-full">
                             <p className="font-bold text-slate-800 tracking-tight text-lg">The Brain Dump</p>
                             <p className="text-slate-600 text-sm mt-1.5 leading-relaxed mb-3">
                                You're carrying too much weight inside your head. Release the pressure.
                             </p>
                             <textarea 
                                 className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none resize-none h-20"
                                 placeholder="Type everything that's stressing you out right now..."
                             />
                         </div>
                     </Card>
                )}

                {(diagnosticState === 'Orange' || diagnosticState === 'Yellow') && (
                     <Card 
                         hover 
                         onClick={() => { onClose(); navigate('/course/mindset_shift'); }}
                         className="p-4 border-amber-200 shadow-sm bg-white text-left flex gap-4 items-center cursor-pointer transition-transform active:scale-95 group hover:-translate-y-1"
                     >
                         <span className="text-3xl drop-shadow-sm group-hover:scale-110 transition-transform">🧠</span>
                         <div>
                             <p className="font-bold text-slate-800 tracking-tight">Review The Foundation</p>
                             <p className="text-slate-500 text-xs mt-0.5">Reset your mindset with our core training.</p>
                         </div>
                     </Card>
                )}

                {diagnosticState === 'Green' && (
                     <Card 
                         hover 
                         onClick={() => { onClose(); navigate('/hub'); }}
                         className="p-4 border-emerald-200 shadow-sm bg-white text-left flex gap-4 items-center cursor-pointer transition-transform active:scale-95 group hover:-translate-y-1"
                     >
                         <span className="text-3xl drop-shadow-sm group-hover:scale-110 transition-transform">🏆</span>
                         <div>
                             <p className="font-bold text-slate-800 tracking-tight">Share a Win</p>
                             <p className="text-slate-500 text-xs mt-0.5">Drop a win in the forum to encourage others.</p>
                         </div>
                     </Card>
                )}
            </div>

            <div className="mb-6">
                <Card 
                    hover 
                    onClick={isSharing ? undefined : handleShareToGroup}
                    className={`p-3 border-indigo-200 shadow-sm bg-indigo-50/50 text-left flex gap-3 items-center transition-transform ${isSharing ? 'opacity-50 cursor-wait' : 'cursor-pointer active:scale-95 group hover:-translate-y-1'}`}
                >
                    <span className="text-2xl drop-shadow-sm group-hover:scale-110 transition-transform">🛡️</span>
                    <div>
                        <p className="font-bold text-indigo-900 tracking-tight text-sm">Share to Stronger Together</p>
                        <p className="text-indigo-700/70 text-[10px] mt-0.5">Post this check-in to the community channel anonymously.</p>
                    </div>
                </Card>
            </div>

            <Button onClick={onClose} disabled={isSharing} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-14 text-base font-bold rounded-[20px] shadow-lg transition-transform active:scale-95">
                Close Check-in
            </Button>
        </div>
    );
};
